import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Switch } from "~/components/ui/switch";

import { centsToDisplay, displayToCents } from "./helpers";
import type { OnlineOrderingFormValues, SectionProps } from "./types";

type BooleanFormKey = {
  [K in keyof OnlineOrderingFormValues]: OnlineOrderingFormValues[K] extends boolean
    ? K
    : never;
}[keyof OnlineOrderingFormValues];

type NumberFormKey = {
  [K in keyof OnlineOrderingFormValues]: OnlineOrderingFormValues[K] extends number
    ? K
    : never;
}[keyof OnlineOrderingFormValues];

// ─── Reusable sub-components ────────────────────────

export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="space-y-1 py-4">
        <h2 className="subtitle-sm text-foreground">{title}</h2>
        <p className="body-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

export function ToggleField({
  form,
  name,
  label,
  description,
  onUpdate,
}: SectionProps & {
  name: BooleanFormKey;
  label: string;
  description: string;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="body-sm text-foreground flex items-center gap-2">
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={(v) => {
                  field.onChange(v);
                  onUpdate();
                }}
              />
            </FormControl>
            <div className="subtitle-sm">
              {label}
              <p className="body-sm text-muted-foreground">{description}</p>
            </div>
          </FormLabel>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function NumberField({
  form,
  name,
  label,
  onUpdate,
}: SectionProps & {
  name: NumberFormKey;
  label: string;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              onBlur={() => onUpdate()}
              onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
              type="number"
              value={field.value}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function DollarField({
  form,
  name,
  label,
  onUpdate,
}: SectionProps & {
  name: NumberFormKey;
  label: string;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className="relative">
              <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm">
                $
              </span>
              <Input
                className="pl-7"
                onBlur={() => onUpdate()}
                onChange={(e) => field.onChange(displayToCents(e.target.value))}
                step="0.01"
                type="number"
                value={centsToDisplay(field.value)}
              />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
